var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, makeObservable } from 'mobx';
import { Spinner } from '../config/common';
import { MandateQueryType, MandateType } from '../config/mandate';
import CustomerMandate from '../config/mandate/CustomerMandate';
import Mandate from '../config/mandate/Mandate';
import API from '../util/ApiUtil';
import { DEFAULT_VALID_UNTIL } from './MandateStore';
import { endOfDay, subSeconds } from 'date-fns';
export default class EditMandateStore {
    constructor(rootStore) {
        Object.defineProperty(this, "originalCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customerMandateForm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new CustomerMandate({})
        });
        Object.defineProperty(this, "validUntil", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "mandateSaveError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isFormChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isManageModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "mandateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "spinnerStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toggleMandateType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.isFormChanged = true;
                return this.removeMandate(value) ? this.handleRemoveMandate(value) : this.addMandate(value);
            }
        });
        Object.defineProperty(this, "handleRemoveMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                if (value === MandateType.BILLING) {
                    this.removeMandate(MandateType.SERVICE_USAGE_DISTINCTION);
                }
                if (this.customerMandateForm.getVisibleMandates().length === 0) {
                    this.removeInvisibleDiilMandates();
                }
            }
        });
        Object.defineProperty(this, "removeInvisibleDiilMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.customerMandateForm.mandates.forEach((mandate) => this.removeMandate(mandate.type));
            }
        });
        Object.defineProperty(this, "addMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                const mandateIndex = this.customerMandateForm.mandates.findIndex((mandate) => mandate.type === value && mandate.isDiilMandate());
                if (mandateIndex < 0) {
                    this.addMandateToForm(value);
                    if (value === MandateType.BILLING) {
                        this.addMandate(MandateType.SERVICE_USAGE_DISTINCTION);
                    }
                }
            }
        });
        Object.defineProperty(this, "addMandateToForm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                let mandate = this.findDiilMandateFromOriginal(value);
                if (!mandate) {
                    mandate = this.newMandate(value);
                }
                this.customerMandateForm.mandates.push(new Mandate({ ...mandate, validUntil: this.validUntil }));
            }
        });
        Object.defineProperty(this, "findDiilMandateFromOriginal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                return this.originalCustomerMandate.mandates.find((mandate) => mandate.isDiilMandate() && mandate.type === value);
            }
        });
        Object.defineProperty(this, "newMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                return new Mandate({ type: value, validFrom: this.getCurrentTimeWithOffset(), diilAccounts: [-1] });
            }
        });
        Object.defineProperty(this, "removeMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                const mandateIndex = this.customerMandateForm.mandates.findIndex((mandate) => mandate.type === value && mandate.isDiilMandate());
                if (mandateIndex > -1) {
                    this.customerMandateForm.mandates.splice(mandateIndex, 1);
                    return true;
                }
            }
        });
        Object.defineProperty(this, "changeVisibleMandatesValidUntils", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value = null) => {
                this.isFormChanged = true;
                this.validUntil = value && endOfDay(value);
                this.customerMandateForm.getVisibleMandates().forEach((mandate) => {
                    mandate.validUntil = this.validUntil;
                });
            }
        });
        Object.defineProperty(this, "openEditCustomerMandateModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (customerMandate) => {
                this.originalCustomerMandate = customerMandate;
                this.customerMandateForm = new CustomerMandate(customerMandate);
                this.validUntil = this.getLatestValidUntilFromFormVisibleMandates();
                this.mandateSaveError = false;
                this.isFormChanged = false;
            }
        });
        Object.defineProperty(this, "getLatestValidUntilFromFormVisibleMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.customerMandateForm.getVisibleMandates().reduce((prev, next) => (prev.validUntil > next.validUntil ? prev : next)).validUntil;
            }
        });
        Object.defineProperty(this, "submitCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await this.saveCustomerMandate();
                return !this.mandateSaveError;
            }
        });
        Object.defineProperty(this, "saveCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    this.spinnerStore.show(Spinner.EDIT_MANDATE_MODAL);
                    await Promise.all([this.updateExistingMandates(), this.addNewMandates()]);
                    await this.mandateStore.fetchMandates(MandateQueryType.CUSTOMER_MANDATES);
                }
                catch (e) {
                    console.error(e);
                    this.handleSaveError();
                }
                finally {
                    this.spinnerStore.hide(Spinner.EDIT_MANDATE_MODAL);
                }
            }
        });
        Object.defineProperty(this, "handleSaveError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.mandateSaveError = true;
            }
        });
        Object.defineProperty(this, "updateExistingMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const customerMandate = new CustomerMandate({ ...this.originalCustomerMandate, mandates: this.getUpdatedMandates() });
                return customerMandate.mandates.length > 0 ? API.put('/mandate/customer-mandate', customerMandate) : Promise.resolve(null);
            }
        });
        Object.defineProperty(this, "getUpdatedMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                let updatedMandates = this.getRemovedDiilMandates().concat(this.getModifiedMandates());
                if (updatedMandates.length > 0) {
                    updatedMandates = updatedMandates.concat(this.getUnmodifiedNonDiilMandates(updatedMandates));
                }
                return updatedMandates;
            }
        });
        Object.defineProperty(this, "getRemovedDiilMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const removedMandates = this.originalCustomerMandate.mandates.filter((mandate) => mandate.isDiilMandate() && this.diilMandateIsNotInCustomerMandateForm(mandate));
                return removedMandates.map((mandate) => {
                    mandate.validUntil = this.getCurrentTimeWithOffset();
                    return mandate;
                });
            }
        });
        Object.defineProperty(this, "diilMandateIsNotInCustomerMandateForm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandate) => {
                return !this.customerMandateForm.mandates.find((formMandate) => formMandate.type === mandate.type && formMandate.isDiilMandate());
            }
        });
        Object.defineProperty(this, "getCurrentTimeWithOffset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return subSeconds(new Date(), 10);
            }
        });
        Object.defineProperty(this, "getModifiedMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.customerMandateForm.mandates.filter((mandate) => mandate.isDiilMandate() && this.isDiilMandateChanged(mandate));
            }
        });
        Object.defineProperty(this, "isDiilMandateChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandate) => {
                return !!this.originalCustomerMandate.mandates.find((formMandate) => formMandate.type === mandate.type && formMandate.isDiilMandate() && this.isMandateValidUntilChanged(formMandate, mandate));
            }
        });
        Object.defineProperty(this, "isMandateValidUntilChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (formMandate, originalMandate) => {
                return (formMandate.validUntil && formMandate.validUntil.getTime()) !== (originalMandate.validUntil && originalMandate.validUntil.getTime());
            }
        });
        Object.defineProperty(this, "getUnmodifiedNonDiilMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandatesToMatch) => {
                return this.customerMandateForm.mandates.filter((formMandate) => !formMandate.isDiilMandate() && mandatesToMatch.find((matchingMandate) => matchingMandate.type === formMandate.type));
            }
        });
        Object.defineProperty(this, "addNewMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const customerMandate = new CustomerMandate({ ...this.originalCustomerMandate, mandates: this.getNewMandates() });
                if (customerMandate.mandates.length > 0) {
                    return API.post('/mandate/customer-mandate', customerMandate);
                }
            }
        });
        Object.defineProperty(this, "getNewMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.customerMandateForm.mandates.filter((mandate) => mandate.isDiilMandate() &&
                    !this.originalCustomerMandate.mandates.find((originalMandate) => originalMandate.type === mandate.type && originalMandate.isDiilMandate()));
            }
        });
        Object.defineProperty(this, "hasMandateType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandateType) => {
                return !!this.customerMandateForm.getVisibleMandates().find((mandate) => mandate.type === mandateType);
            }
        });
        Object.defineProperty(this, "getMandateTypes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const mandateTypes = [MandateType.SERVICE, MandateType.BILLING];
                if (this.customerMandateForm.mandates.find((mandate) => mandate.isDiilMandate() && [MandateType.BILLING, MandateType.SERVICE_USAGE_DISTINCTION].includes(mandate.type))) {
                    mandateTypes.push(MandateType.SERVICE_USAGE_DISTINCTION);
                }
                return mandateTypes;
            }
        });
        Object.defineProperty(this, "openManageModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (customerMandate) => {
                this.openEditCustomerMandateModal(customerMandate);
                this.isManageModalOpen.set(customerMandate.redisId, true);
            }
        });
        Object.defineProperty(this, "closeManageModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (customerMandate) => {
                this.isManageModalOpen.set(customerMandate.redisId, false);
            }
        });
        makeObservable(this);
        this.mandateStore = rootStore.mandateStore;
        this.spinnerStore = rootStore.spinnerStore;
        this.validUntil = DEFAULT_VALID_UNTIL;
    }
    get canEditCustomerMandate() {
        return !!this.validUntil;
    }
    get isValidUntilDisabled() {
        return this.customerMandateForm.getVisibleMandates().length === 0;
    }
}
__decorate([
    observable
], EditMandateStore.prototype, "originalCustomerMandate", void 0);
__decorate([
    observable
], EditMandateStore.prototype, "customerMandateForm", void 0);
__decorate([
    observable
], EditMandateStore.prototype, "validUntil", void 0);
__decorate([
    observable
], EditMandateStore.prototype, "mandateSaveError", void 0);
__decorate([
    observable
], EditMandateStore.prototype, "isFormChanged", void 0);
__decorate([
    observable
], EditMandateStore.prototype, "isManageModalOpen", void 0);
__decorate([
    computed
], EditMandateStore.prototype, "canEditCustomerMandate", null);
__decorate([
    computed
], EditMandateStore.prototype, "isValidUntilDisabled", null);
__decorate([
    action.bound
], EditMandateStore.prototype, "toggleMandateType", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "changeVisibleMandatesValidUntils", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "openEditCustomerMandateModal", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "saveCustomerMandate", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "handleSaveError", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "openManageModal", void 0);
__decorate([
    action.bound
], EditMandateStore.prototype, "closeManageModal", void 0);
